import { useEffect, useState } from 'react';
import { usePublicClient } from 'wagmi';
import type { Address, PublicClient } from 'viem';
import { environment, type FactoryAbiVersion } from '@/config/environment';
import { TOKEN_LOCK_FACTORY_ABI } from '@/contracts/TokenLockFactory.abi';
import { TOKEN_LOCK_ABI } from '@/contracts/TokenLock.abi';
import { ERC20_ABI } from '@/contracts/ERC20.abi';
import { createRpcReader } from '@/utils/rpcRead';

export interface GrantSummary {
  asOf: bigint;
  grantCount: number;
  activeGrantCount: number;
  totalAllocation: bigint;
  factoryBalance: bigint;
  funding: {
    nextUnlockTime: bigint;
    nextPeriodFunding: bigint;
    remainingFunding: bigint;
    unfundedEarned: bigint;
  } | null;
}

/** Full-roster snapshot, independent of the partially loaded details table. */
export async function readGrantSummary(
  client: PublicClient,
  factory: Address,
  version: FactoryAbiVersion,
  signal?: AbortSignal,
): Promise<GrantSummary> {
  const read = createRpcReader(signal);
  const block = await read(() => client.getBlock({ blockTag: 'latest' }));
  const blockNumber = block.number;
  const locks = await read(() => client.readContract({
    address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'getAllLockContracts', blockNumber,
  }));
  const supportsFunding = version !== 'v7';
  let totalAllocation = 0n;
  let activeGrantCount = 0;
  let nextUnlockTime = 0n;

  // Only lightweight fields for totals; don't load every grant's full details.
  // Unlike a details page, ANY failed read must reject the entire summary.
  for (let offset = 0; offset < locks.length; offset += 10) {
    const page = locks.slice(offset, offset + 10);
    const contracts = page.flatMap((address) => [
      { address, abi: TOKEN_LOCK_ABI, functionName: 'totalAmount' } as const,
      ...(version === 'v10' ? [{ address, abi: TOKEN_LOCK_ABI, functionName: 'cancelled' } as const] : []),
      ...(supportsFunding ? [{ address, abi: TOKEN_LOCK_ABI, functionName: 'nextUnlockTime' } as const] : []),
    ]);
    const values = await read(() => client.multicall({
      contracts, allowFailure: false, batchSize: 0, blockNumber,
    }));
    let cursor = 0;
    for (let index = 0; index < page.length; index++) {
      const amount = values[cursor++];
      const cancelled = version === 'v10' ? values[cursor++] : false;
      const next = supportsFunding ? values[cursor++] : 0n;
      if (typeof amount !== 'bigint' || typeof cancelled !== 'boolean' || typeof next !== 'bigint') {
        throw new Error('Incomplete grant summary response');
      }
      if (cancelled) continue;
      totalAllocation += amount;
      activeGrantCount++;
      if (next > block.timestamp && (nextUnlockTime === 0n || next < nextUnlockTime)) nextUnlockTime = next;
    }
  }

  const token = await read(() => client.readContract({
    address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'token', blockNumber,
  }));
  const factoryBalance = await read(() => client.readContract({
    address: token, abi: ERC20_ABI, functionName: 'balanceOf', args: [factory], blockNumber,
  }));
  let funding: GrantSummary['funding'] = null;
  if (supportsFunding) {
    const remainingFunding = await read(() => client.readContract({
      address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'totalRemainingObligation', blockNumber,
    }));
    const unfundedEarned = await read(() => client.readContract({
      address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'totalUnfundedEarned', blockNumber,
    }));
    // nextFundingDue() can mean "now" or the 100-day funding horizon, rather
    // than the next release. Quote the actual next release (including arrears).
    // This view accepts future dates beyond the transaction's funding horizon.
    const nextPeriodFunding = await read(() => client.readContract({
      address: factory, abi: TOKEN_LOCK_FACTORY_ABI, functionName: 'totalFundingRequiredThrough',
      args: [nextUnlockTime || block.timestamp], blockNumber,
    }));
    funding = { nextUnlockTime, nextPeriodFunding, remainingFunding, unfundedEarned };
  }
  return { asOf: block.timestamp, grantCount: locks.length, activeGrantCount, totalAllocation, factoryBalance, funding };
}

export function useGrantSummary(revision: number) {
  const client = usePublicClient({ chainId: environment.chainId });
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({
    revision, data: null as GrantSummary | null, error: null as string | null,
  });
  useEffect(() => {
    const controller = new AbortController();
    setState({ revision, data: null, error: null });
    if (client) {
      readGrantSummary(client, environment.factoryAddress as Address, environment.factoryAbiVersion, controller.signal)
        .then((data) => {
          if (!controller.signal.aborted) setState({ revision, data, error: null });
        })
        .catch(() => {
          if (!controller.signal.aborted) setState({ revision, data: null,
            error: 'Unable to load the complete summary. Please retry.' });
        });
    }
    return () => controller.abort();
  }, [client, revision, attempt]);
  return {
    data: state.revision === revision ? state.data : null,
    error: state.revision === revision ? state.error : null,
    retry: () => setAttempt((value) => value + 1),
  };
}
